import { useQuery } from '@apollo/client'
import { graphql } from '~publish/graphql'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { SavedReply, SavedRepliesQueryQuery } from '~publish/gql/graphql'

type UseSavedRepliesReturn = {
  savedReplies: SavedReply[] | null
  error: string | null
  loading: boolean
}

export const SavedRepliesQuery = graphql(/* GraphQL */ `
  query SavedRepliesQuery($input: SavedRepliesInput!) {
    savedReplies(input: $input) {
      savedReplies {
        id
        organizationId
        title
        content {
          text
        }
        createdAt
        updatedAt
      }
    }
  }
`)

/**
 * Hook to fetch the saved replies for a given organization.
 * @returns {UseSavedRepliesReturn} An object containing the fetchSavedReplies function
 *
 * @example
 * const { fetchSavedReplies } = useSavedReplies()
 * const { data, loading, error } = await fetchSavedReplies({
 *   organizationId: '123',
 * })
 */
export function useSavedRepliesQuery(): UseSavedRepliesReturn {
  const organizationId = useCurrentOrganization()?.id ?? ''

  const { data, loading, error } = useQuery<SavedRepliesQueryQuery>(
    SavedRepliesQuery,
    {
      variables: {
        input: { organizationId },
      },
      fetchPolicy: 'cache-first',
      skip: !organizationId,
    },
  )

  if (loading) return { savedReplies: [], error: null, loading: true }

  let _error: string | null = null

  if (error) {
    _error = 'Error fetching saved replies'
  }

  return {
    savedReplies: data?.savedReplies.savedReplies ?? null,
    error: _error,
    loading,
  }
}
